// client/src/pages/admin/project-steps/Step9Offers.tsx
import React from 'react';
import { ProjectForm } from './types';
import SectionInfoBox from './SectionInfoBox';
import { motion, AnimatePresence } from 'framer-motion';

interface Props {
    form: ProjectForm;
    setForm: React.Dispatch<React.SetStateAction<ProjectForm>>;
    errors?: Record<string, boolean>;
    QuickUpdateButton?: React.FC;
    packageFeatures?: any;
    scoreSettings?: any;
}

const Step9Offers: React.FC<Props> = ({ form, setForm, errors, QuickUpdateButton, packageFeatures }) => {
    const maxOffers = packageFeatures === undefined ? 100 : (packageFeatures?.projectOffers || 0);
    const isUnlimited = maxOffers >= 999;
    const canAddOffer = isUnlimited || form.builderOffers.length < maxOffers;
    return (
        <div className="space-y-6">
            <div className="flex items-center gap-4 mb-4 px-2">
                <div className="w-10 h-10 rounded-full bg-blue-600 text-white flex items-center justify-center font-bold text-lg shadow-md">
                    9
                </div>
                <div>
                    <h5 className="text-xl font-bold text-gray-800">Project Offers</h5>
                    <p className="text-[10px] font-bold text-gray-400 uppercase tracking-widest">Manage special deals, discounts, and promotional offers</p>
                </div>
            </div>

            <section className="bg-white border border-gray-150 p-6 md:p-8 rounded-2xl shadow-sm space-y-6">
                <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                        <div className="w-8 h-8 rounded-lg bg-blue-50 flex items-center justify-center text-blue-600">
                            <i className="fa-solid fa-gift text-sm"></i>
                        </div>
                        <h6 className="text-sm font-bold text-gray-800">Available Offers</h6>
                    </div>
                    {maxOffers === 0 ? (
                        <div className="px-4 py-2 bg-slate-50 text-slate-500 rounded-xl text-xs font-bold border border-slate-200 flex items-center gap-2 cursor-not-allowed">
                            <i className="fa-solid fa-lock"></i> Upgrade Package for Offers
                        </div>
                    ) : canAddOffer ? (
                        <button 
                            type="button" 
                            className="px-4 py-2 bg-blue-50 text-blue-600 rounded-xl text-xs font-bold transition-all border border-blue-900/5 hover:bg-blue-100 flex items-center gap-2" 
                            onClick={() => setForm({ ...form, builderOffers: [...form.builderOffers, { offerType: '', example: '', priceImpact: '', startDate: '', tillDate: '' }] })}
                        >
                            <i className="fa-solid fa-plus"></i> Add Offer {isUnlimited ? '' : `(${form.builderOffers.length}/${maxOffers})`}
                        </button>
                    ) : (
                        <div className="px-4 py-2 bg-red-50 text-red-500 rounded-xl text-xs font-bold border border-red-200 flex items-center gap-2 cursor-not-allowed">
                            <i className="fa-solid fa-ban"></i> Max {maxOffers} Offer(s) Reached
                        </div>
                    )}
                </div>

                <div className={`grid grid-cols-1 md:grid-cols-2 gap-6 ${errors?.builderOffers ? 'p-4 rounded-xl border border-rose-500 bg-rose-50' : ''}`}>
                    <AnimatePresence>
                        {form.builderOffers.map((offer, idx) => (
                            <motion.div 
                                key={idx}
                                initial={{ opacity: 0, y: 20 }}
                                animate={{ opacity: 1, y: 0 }}
                                exit={{ opacity: 0, scale: 0.9 }}
                                className={`p-6 rounded-2xl border relative group transition-all ${errors?.builderOffers ? 'border-rose-500 bg-white' : 'bg-gray-50 border-gray-200 hover:border-blue-300 shadow-sm'}`}
                            >
                                <button 
                                    type="button" 
                                    className="absolute -top-3 -right-3 w-8 h-8 rounded-full bg-white border border-gray-200 text-gray-400 hover:text-rose-500 hover:border-rose-200 flex items-center justify-center transition-all shadow-sm z-20 active:scale-95" 
                                    onClick={() => setForm({ ...form, builderOffers: form.builderOffers.filter((_, i) => i !== idx) })}
                                >
                                    <i className="fa-solid fa-xmark text-sm"></i>
                                </button>
                                
                                <div className="grid grid-cols-1 gap-5">
                                    <div className="space-y-2">
                                        <label className="text-xs font-bold text-gray-600 block">Offer Type <span className="text-rose-500">*</span></label>
                                        <div className="relative flex items-center">
                                            <span className="absolute left-4 text-gray-400">
                                                <i className="fa-solid fa-tag text-xs"></i>
                                            </span>
                                            <input 
                                                type="text" 
                                                placeholder="e.g. Cash Discount" 
                                                value={offer.offerType} 
                                                onChange={e => {
                                                    const no = [...form.builderOffers]; no[idx].offerType = e.target.value; setForm({ ...form, builderOffers: no });
                                                }} 
                                                className={`w-full pl-10 pr-4 py-3 rounded-xl bg-white border ${errors?.builderOffers ? 'border-rose-500 text-rose-500' : 'border-gray-200 text-gray-800'} focus:border-blue-500 transition-all font-semibold outline-none text-sm`} 
                                            />
                                        </div>
                                    </div>

                                    <div className="space-y-2">
                                        <label className="text-xs font-bold text-gray-600 block">Offer Value <span className="text-rose-500">*</span></label>
                                        <div className="relative flex items-center">
                                            <span className="absolute left-4 text-emerald-500">
                                                <i className="fa-solid fa-indian-rupee-sign text-xs"></i>
                                            </span>
                                            <input 
                                                type="text" 
                                                placeholder="e.g. ₹5 LAKHS DISCOUNT" 
                                                value={offer.example} 
                                                onChange={e => {
                                                    const no = [...form.builderOffers]; no[idx].example = e.target.value; setForm({ ...form, builderOffers: no });
                                                }} 
                                                className={`w-full pl-10 pr-4 py-3 rounded-xl bg-white border ${errors?.builderOffers ? 'border-rose-500 text-rose-500' : 'border-gray-200 text-gray-800'} focus:border-blue-500 transition-all font-semibold outline-none text-sm`} 
                                            />
                                        </div>
                                    </div>

                                    <div className="space-y-2">
                                        <label className="text-xs font-bold text-gray-600 block">Offer Description</label>
                                        <div className="relative flex items-center">
                                            <span className="absolute left-4 text-gray-400">
                                                <i className="fa-solid fa-circle-info text-xs"></i>
                                            </span>
                                            <input 
                                                type="text" 
                                                placeholder="e.g. Applicable on total cost" 
                                                value={offer.priceImpact} 
                                                onChange={e => {
                                                    const no = [...form.builderOffers]; no[idx].priceImpact = e.target.value; setForm({ ...form, builderOffers: no });
                                                }} 
                                                className={`w-full pl-10 pr-4 py-3 rounded-xl bg-white border ${errors?.builderOffers ? 'border-rose-500 text-rose-500' : 'border-gray-200 text-gray-800'} focus:border-blue-500 transition-all font-semibold outline-none text-sm`} 
                                            />
                                        </div>
                                    </div>

                                    <div className="grid grid-cols-2 gap-4">
                                        <div className="space-y-2">
                                            <label className="text-xs font-bold text-gray-600 block">Start Date</label>
                                            <div className="relative flex items-center">
                                                <span className="absolute left-4 text-blue-500">
                                                    <i className="fa-solid fa-calendar-days text-xs"></i>
                                                </span>
                                                <input 
                                                    type="date" 
                                                    className={`w-full pl-10 pr-3 py-3 rounded-xl bg-white border ${errors?.builderOffers ? 'border-rose-500 text-rose-500' : 'border-gray-200 text-gray-800'} focus:border-blue-500 transition-all font-semibold outline-none text-xs`} 
                                                    value={offer.startDate} 
                                                    onChange={e => {
                                                        const no = [...form.builderOffers]; no[idx].startDate = e.target.value; setForm({ ...form, builderOffers: no });
                                                    }} 
                                                />
                                            </div>
                                        </div>
                                        <div className="space-y-2">
                                            <label className="text-xs font-bold text-gray-600 block">End Date</label>
                                            <div className="relative flex items-center">
                                                <span className="absolute left-4 text-rose-500">
                                                    <i className="fa-solid fa-calendar-xmark text-xs"></i>
                                                </span>
                                                <input 
                                                    type="date" 
                                                    className={`w-full pl-10 pr-3 py-3 rounded-xl bg-white border ${errors?.builderOffers ? 'border-rose-500 text-rose-500' : 'border-gray-200 text-gray-800'} focus:border-blue-500 transition-all font-semibold outline-none text-xs`} 
                                                    value={offer.tillDate} 
                                                    onChange={e => {
                                                        const no = [...form.builderOffers]; no[idx].tillDate = e.target.value; setForm({ ...form, builderOffers: no });
                                                    }} 
                                                />
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </motion.div>
                        ))}
                    </AnimatePresence>
                </div>
            </section>

        </div>
    );
};

export default Step9Offers;
